'use client';

import { useMemo } from 'react';
import { useCandidateNavigation } from '@components/contexts/CandidateNavigationContext';

/**
 * @description Hook to get job search filters based on the current candidate view.
 * Returns filters for the selected job alert, keyword search, or undefined for "new search" (default jobs).
 */
export const useJobSearchFiltersFromNavigation = () => {
  const { currentView } = useCandidateNavigation();

  const filters = useMemo(() => {
    if (currentView === 'new-search' || currentView === 'browse-all') {
      return undefined;
    }

    if (currentView === 'new-job-search-form' || (typeof currentView === 'object' && 'editSearch' in currentView)) {
      return undefined;
    }

    // Keyword search (from last experience or next job)
    if (typeof currentView === 'object' && 'keyword' in currentView) {
      return { keyword: currentView.keyword };
    }

    // Candidate search (includes alert clicks — alerts are converted to candidateSearch on click)
    if (typeof currentView === 'object' && 'candidateSearch' in currentView) {
      const { keyword, locations } = currentView.candidateSearch;
      const urlParams: Record<string, string | string[] | number[]> = {};
      if (keyword) urlParams.keyword = keyword;
      if (locations?.length) urlParams.location = locations;
      return urlParams;
    }

    return undefined;
  }, [currentView]);

  return {
    filters,
    isNewSearch: currentView === 'new-search',
    isJobSearchForm: currentView === 'new-job-search-form' || (typeof currentView === 'object' && 'editSearch' in currentView),
  };
};
